import moment from 'moment'
import classNames from 'classnames'
import _, { compact, split, trim, toUpper } from 'lodash'
import { useTranslation } from 'react-i18next'
import { MailIcon, PhoneIcon, GlobeAltIcon, OfficeBuildingIcon, CalendarIcon, BanIcon } from '@heroicons/react/outline'
import { getCountryDisplayName } from 'pages/company/modals/countries'
import LoadingIcon from 'components/atoms/LoadingIcon'

const primaryText = 'text-[#091c2d]'
const onSurfaceVariant = 'text-[#454652]'
const onPrimaryContainer = 'text-[#8799af]'

const nationalityBadgeCode = nationality => {
  const s = trim(nationality || '')
  if (!s) return '—'
  const letters = s.replace(/[^a-zA-Z]/g, '')
  if (letters.length >= 2) return toUpper(letters.slice(0, 2))
  return toUpper(s.slice(0, 2))
}

const getInitials = name => {
  if (!name || !_.isString(name)) return '?'
  const parts = compact(split(trim(name), /\s+/))
  if (parts.length >= 2) return toUpper(`${parts[0][0]}${parts[parts.length - 1][0]}`)
  return toUpper(parts[0].slice(0, 2))
}

const BookedUsersCards = ({ users, loadingMore, hasMore, fetchData }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const formatNationality = nationality => {
    if (!nationality) {
      return t('users.cards.nationalityNotSpecified')
    }
    if (i18n.language === 'ar') {
      return getCountryDisplayName(nationality, 'ar') || nationality
    }
    return nationality
  }

  if (!users || users.length === 0) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-slate-200/80 bg-gradient-to-b from-[#f2f4f6]/90 to-white px-6 py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-[#eceef0] text-[#454652] ring-1 ring-slate-200/80">
          <GlobeAltIcon className="h-7 w-7" strokeWidth={1.25} />
        </div>
        <p className="max-w-sm text-base font-medium text-[#091c2d]">{t('users.cards.noBookedUsers')}</p>
      </div>
    )
  }

  return (
    <div className="pb-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {users.map((user, index) => {
          const displayName = user?.fullName || t('users.cards.unknownUser')
          const email = user?.email || t('users.cards.noEmail')
          const hasPhone = Boolean(trim(user?.phone || ''))
          const phone = hasPhone ? user.phone : null
          const natBadge = nationalityBadgeCode(user?.nationality)

          return (
            <article
              key={user._id || index}
              className={classNames(
                'group relative overflow-hidden rounded-xl border border-slate-200/80 bg-white px-6 py-5 text-left shadow-sm transition-all duration-300',
                'hover:border-slate-300/90 hover:shadow-[0_32px_64px_-12px_rgba(9,28,45,0.08)]'
              )}
            >
              <div className="absolute right-0 top-0 p-4">
                <span className={classNames('rounded px-2 py-1 text-xs font-bold uppercase tracking-tighter text-[#454652]', 'bg-[#eceef0]')}>{natBadge}</span>
              </div>

              <div className={classNames('flex items-start gap-5', isRTL && 'flex-row-reverse text-right')}>
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-[#f2f4f6]">
                  {user?.avatar ? (
                    <img className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" src={user.avatar} alt="" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#091c2d] to-[#1f3143]">
                      <span className="text-xl font-bold text-white">{getInitials(displayName)}</span>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1 pr-14">
                  <h3 className={classNames('truncate text-xl font-bold', primaryText)}>{displayName}</h3>

                  <div className="mt-3 space-y-2">
                    <div className={classNames('flex items-center gap-2', onSurfaceVariant, isRTL && 'flex-row-reverse')}>
                      <MailIcon className="h-[18px] w-[18px] shrink-0 opacity-90" strokeWidth={1.75} aria-hidden />
                      <span className="truncate text-sm font-medium" dir="ltr">
                        {email}
                      </span>
                    </div>

                    {hasPhone ? (
                      <div className={classNames('flex items-center gap-2', onSurfaceVariant, isRTL && 'flex-row-reverse')}>
                        <PhoneIcon className="h-[18px] w-[18px] shrink-0 opacity-90" strokeWidth={1.75} aria-hidden />
                        <span className="truncate text-sm font-medium" dir="ltr">
                          {phone}
                        </span>
                      </div>
                    ) : (
                      <div className={classNames('flex items-center gap-2 italic', onPrimaryContainer, isRTL && 'flex-row-reverse')}>
                        <BanIcon className="h-[18px] w-[18px] shrink-0 opacity-70" strokeWidth={1.75} aria-hidden />
                        <span className="text-sm font-medium">{t('users.cards.noPhone')}</span>
                      </div>
                    )}

                    <div className={classNames('flex items-center gap-2', onSurfaceVariant, isRTL && 'flex-row-reverse')}>
                      <GlobeAltIcon className="h-[18px] w-[18px] shrink-0 opacity-90" strokeWidth={1.75} aria-hidden />
                      <span className="text-sm font-medium">
                        <span className={onPrimaryContainer}>{t('users.cards.labelNationality')}: </span>
                        <span className="text-[#8799af]">{formatNationality(user?.nationality)}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {(user?.company?.length > 0 || user?.branch?.length > 0) && (
                <div className={classNames('mt-3', isRTL && 'text-right')}>
                  {user?.company?.length > 0 && (
                    <div className={classNames('flex gap-2', onSurfaceVariant, isRTL && 'flex-row-reverse')}>
                      <OfficeBuildingIcon className="mt-0.5 h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
                      <div className="min-w-0 text-sm font-medium">
                        <span className="text-[#8799af]">{user.company[0].name}</span>
                        {user?.branch?.length > 0 ? (
                          <span className="mt-0.5 block text-xs text-[#454652]/90">
                            {user.branch
                              .map(b => b?.name)
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {user?.createdAt && (
                <div className={classNames('mt-2 flex items-start gap-2', onSurfaceVariant, isRTL && 'flex-row-reverse')}>
                  <CalendarIcon className="mt-0.5 h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
                  <span className="text-sm">
                    <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-[#8799af]">{t('users.cards.labelJoined')}</span>
                    <span className="font-medium text-[#454652]">{moment(user.createdAt).fromNow()}</span>
                  </span>
                </div>
              )}

              {user?.roles && _.reject(user.roles, { name: 'platform_admin' }).length > 0 && (
                <div className={classNames('mt-2 flex flex-wrap gap-1.5', isRTL && 'justify-end')}>
                  {_.map(_.sortBy(_.reject(user.roles, { name: 'platform_admin' }), 'name'), (role, roleIndex) => (
                    <span
                      key={roleIndex}
                      className="inline-flex items-center rounded-lg border border-[#e0e3e5] bg-[#f7f9fb] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#516578]"
                    >
                      {role.name}
                    </span>
                  ))}
                </div>
              )}

              {(user?.verified || user?.active || user?.blacklisted) && (
                <div className={classNames('mt-2 flex flex-wrap gap-2', isRTL && 'justify-end')}>
                  {user?.verified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      {t('users.cards.statusVerified')}
                    </span>
                  )}
                  {user?.active && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-800 ring-1 ring-sky-200/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-sky-500" aria-hidden />
                      {t('users.cards.statusActive')}
                    </span>
                  )}
                  {user?.blacklisted && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-800 ring-1 ring-rose-200/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-500" aria-hidden />
                      {t('users.cards.statusBlacklisted')}
                    </span>
                  )}
                </div>
              )}
            </article>
          )
        })}
      </div>

      {hasMore && (
        <div className="relative z-[1200] mt-8 flex justify-center px-4 lg:mb-12 2xl:mb-4">
          <button
            type="button"
            onClick={() => fetchData()}
            className="inline-flex min-w-[10rem] items-center justify-center gap-2 rounded-xl bg-[#091c2d] px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-[#142a3f] focus:outline-none focus:ring-2 focus:ring-[#091c2d] focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <LoadingIcon className="h-5 w-5" />
                <span>{t('common.loading')}</span>
              </>
            ) : (
              <span>{t('users.cards.loadMoreUsers')}</span>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

export default BookedUsersCards
